/*
 * Seleção em massa (para quem modera): um botão na barra da
 * lista liga caixas de seleção em cada tópico e uma barra de ações acima da
 * lista — marcar como lido, fixar, trancar, alterar etiquetas, ocultar,
 * excluir.
 *
 * Tudo por ItemLists do core: o botão em `IndexPage.actionItems` (que a barra
 * de navegação do tema desenha à direita), a barra em `contentItems`, e a
 * caixa em `DiscussionListItem.contentItems`.
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
import { t } from '../../../utils/settings';
import { bulkActions } from './actions';
import { bulkAvailable, loadedDiscussions, selection } from './selection';

/** O MinimalDiscussionListItem (resultados do modal) herda do item; ali não há seleção. */
function isMinimal(item) {
  return String(item.elementAttrs().className || '').includes('MinimalDiscussionListItem');
}
function toggleButton() {
  const label = selection.active ? t('extras.bulk.toggle_off') : t('extras.bulk.toggle_on');
  return m(Tooltip, {
    text: label
  }, m(Button, {
    className: classList('Button Button--icon CanelaBulk-toggle', {
      active: selection.active
    }),
    icon: "fas fa-list-check",
    "aria-label": label,
    "aria-pressed": selection.active ? 'true' : 'false',
    onclick: () => selection.setActive(!selection.active)
  }));
}
function bar() {
  const selected = selection.selected();
  const count = selected.length;
  const total = loadedDiscussions().length;
  const actions = bulkActions().filter(action => selected.some(discussion => action.applies(discussion)));
  return m("div", {
    className: "CanelaBulkBar",
    role: "toolbar",
    "aria-label": t('extras.bulk.bar_label')
  }, m("div", {
    className: "CanelaBulkBar-status"
  }, m("span", {
    className: "CanelaBulkBar-count"
  }, t('extras.bulk.selected', {
    count
  })), count < total ? m(Button, {
    className: "Button Button--link CanelaBulkBar-link",
    onclick: () => selection.selectAll()
  }, t('extras.bulk.select_all', {
    count: total
  })) : null, count > 0 && m(Button, {
    className: "Button Button--link CanelaBulkBar-link",
    onclick: () => selection.clear()
  }, t('extras.bulk.clear'))), m("div", {
    className: "CanelaBulkBar-actions"
  }, count === 0 && m("span", {
    className: "CanelaBulkBar-hint"
  }, t('extras.bulk.hint')), actions.map(action => m(Button, {
    className: classList('Button CanelaBulkBar-action', {
      'CanelaBulkBar-action--danger': action.danger
    }),
    icon: action.icon,
    onclick: () => action.run(selection.selected())
  }, action.label)), m(Button, {
    className: "Button Button--icon Button--link CanelaBulkBar-close",
    icon: "fas fa-times",
    "aria-label": t('extras.bulk.toggle_off'),
    onclick: () => selection.setActive(false)
  })));
}
export default function installBulkSelect() {
  extend(IndexPage.prototype, 'oninit', function () {
    // Outra lista, outra seleção: marcar tópicos de uma tag e trocar de tag
    // não pode deixar a ação valendo para o que sumiu da tela.
    selection.setActive(false);
  });
  extend(IndexPage.prototype, 'actionItems', function (items) {
    if (!bulkAvailable()) return;
    items.add('canelaBulk', toggleButton(), 5);
  });
  extend(IndexPage.prototype, 'contentItems', function (items) {
    if (!selection.active || !bulkAvailable()) return;

    // Entre a barra de navegação (100) e a lista (90).
    items.add('canelaBulkBar', bar(), 95);
  });
  extend(DiscussionListItem.prototype, 'oninit', function () {
    // O item só se redesenha quando o SubtreeRetainer vê mudança; a seleção
    // precisa entrar nessa conta, senão a caixa não reage ao clique.
    this.subtree.check(() => selection.active, () => selection.active && selection.has(this.attrs.discussion));
  });
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    if (!selection.active || isMinimal(this)) return;
    const discussion = this.attrs.discussion;
    const checked = selection.has(discussion);
    items.add('canelaBulkCheck', m("label", {
      className: "CanelaBulkCheck",
      onclick: event => event.stopPropagation()
    }, m("input", {
      type: "checkbox",
      checked: checked,
      "aria-label": t('extras.bulk.select_one', {
        title: discussion.title()
      }),
      onclick: event => {
        selection.toggle(discussion, event.shiftKey);

        // Com shift+clique o estado final vem do intervalo, não do clique:
        // a caixa clicada é acertada aqui, porque o item pode nem se
        // redesenhar (SubtreeRetainer) se o estado dele não mudou.
        event.currentTarget.checked = selection.has(discussion);
      }
    })),
    // Antes de tudo, inclusive do avatar do celular (100).
    110);
  });
  extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    if (selection.active && selection.has(this.attrs.discussion)) {
      attrs.className = classList(attrs.className, 'CanelaBulk-selected');
    }
  });
}
export { selection };
export const bulkHasSelection = () => selection.count() > 0 && !!app.session.user;
flarum.reg.add('ramon-canela', 'forum/features/extras/bulk', installBulkSelect);